// src/prototype/components/sep/SEPSectionDependenciesRisk.tsx
// Component 9: risks_and_dependencies

import React from 'react';
import { AlertTriangle, GitMerge } from 'lucide-react';
import type { MappedRisksAndDependencies } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionDependenciesRiskProps {
  clientName: string;
  risksAndDependencies: MappedRisksAndDependencies | null;
}

const levelColor = (level: string) =>
  ({
    Low: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    Medium: 'text-amber-700 bg-amber-50 border-amber-100',
    High: 'text-rose-600 bg-rose-50 border-rose-100',
    Critical: 'text-rose-700 bg-rose-50 border-rose-100',
    Standard: 'text-[#18365E] bg-[#18365E]/5 border-[#18365E]/10',
  })[level] ?? 'text-gray-600 bg-gray-50 border-gray-200';

export const SEPSectionDependenciesRisk: React.FC<SEPSectionDependenciesRiskProps> = ({
  clientName, risksAndDependencies,
}) => {
  const sharedDependencies = risksAndDependencies?.sharedDependencies ?? [];
  const riskByInitiative = risksAndDependencies?.riskByInitiative ?? [];
  const riskRows = riskByInitiative.flatMap(group =>
    group.risks.map(risk => ({
      ...risk,
      initiativeId: group.id,
      initiativeName: group.initiativeName,
    }))
  );

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 7</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Dependencies & Risk Summary</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Shared portfolio dependencies and initiative-level risk mitigation</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The risk register below captures key execution risks across <strong>{clientName}</strong>'s initiative portfolio,
        along with mitigation strategies. Shared dependencies are highlighted to support coordinated planning across initiatives.
      </p>

      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <GitMerge className="w-3.5 h-3.5 text-[#18365E]/60" />
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Shared Portfolio Dependencies</p>
        </div>
        {sharedDependencies.length > 0 ? (
          <div className="space-y-2.5">
            {sharedDependencies.map(dep => (
              <div key={dep.id} className="border border-[#e5e7eb] rounded-xl p-3.5 bg-[#F5F7FA]">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#18365E] mt-1.5 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold text-[#18365E]">{dep.title}</p>
                      {dep.priority && (
                        <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border ${levelColor(dep.priority)}`}>
                          {dep.priority}
                        </span>
                      )}
                      <span className="text-[9px] text-[#717182]">{dep.id}</span>
                    </div>
                  </div>
                </div>
                {dep.relatedInitiatives.length > 0 && (
                  <div className="flex flex-wrap gap-1 pl-3.5">
                    {dep.relatedInitiatives.map(init => (
                      <span key={init} className="text-[10px] px-2 py-0.5 bg-white border border-[#e5e7eb] rounded-full text-[#717182]">
                        {init}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Shared dependency data is not available.</p>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-3.5 h-3.5 text-[#717182]/60" />
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Risk Register</p>
        </div>
        {riskRows.length > 0 ? (
          <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[#717182]">Risk Name</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-[#717182] w-24">Likelihood</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-[#717182] w-20">Impact</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Mitigation Plan</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-[#717182] w-32">Risk Owner</th>
                </tr>
              </thead>
              <tbody>
                {riskRows.map(risk => (
                  <tr key={risk.id} className="border-b border-[#e5e7eb]/60 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#18365E]">{risk.name}</p>
                      <p className="text-[10px] text-[#717182] mt-0.5">{risk.initiativeName}</p>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${levelColor(risk.likelihood)}`}>
                        {risk.likelihood}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${levelColor(risk.impact)}`}>
                        {risk.impact}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[#18365E]/75 leading-relaxed">{risk.mitigation}</td>
                    <td className="px-3 py-3 text-[#717182]">{risk.owner || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Risk register data is not available.</p>
        )}
      </div>
    </div>
  );
};

export default SEPSectionDependenciesRisk;
